import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { setCredentials, logOut } from "../auth/authSlice";

const toNumber = (value) => {
  if (typeof value === 'number') return value;
  if (value === undefined || value === null || value === '') return 0;
  const parsed = parseFloat(String(value).replace(/[^0-9.,-]/g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

const calculateAdditionalPrice = (item) => {
  if (!Array.isArray(item?.customOptions)) return 0;
  return item.customOptions.reduce((total, option) => total + toNumber(option?.additionalPrice), 0);
};

const recomputeCartTotals = (state) => {
  state.total = state.products.reduce((cartTotal, cartItem) => cartTotal + cartItem.totalPrice, 0);
  state.quantity = state.products.reduce((cartQuantity, cartItem) => cartQuantity + cartItem.quantity, 0);
  state.totalTax = state.products.reduce(
    (totalTax, product) => totalTax + (product.totalPrice * toNumber(product.taxPercentage)) / 100,
    0
  );
};

const repriceCartForMembership = (state, isPremium) => {
  state.products.forEach((item) => {
    // Preserve a fallback regular unit price for future repricing.
    if (item.regularPrice === undefined || item.regularPrice === null || item.regularPrice === '') {
      item.regularPrice = item.price;
    }

    const regularPrice = toNumber(item.regularPrice);
    const premiumPrice = toNumber(item.premiumPrice);
    const nextUnitPrice = isPremium && premiumPrice > 0 ? premiumPrice : regularPrice;

    item.price = nextUnitPrice;
    item.totalPrice = (nextUnitPrice + calculateAdditionalPrice(item)) * item.quantity;
  });

  recomputeCartTotals(state);
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    products: [],
    quantity: 0,
    formData: null,
    partnerPrice: 6000,
    partnerTax: 25,
    total: 0,
    checkoutId: null,
    email: '',
    customerMessage: '',
    activeStep: 0,
    billing: null,
    totalTax: 0,
    currentPartner: null,
  },
  reducers: {
    addProduct: (state, action) => {
      const newItem = {
        ...action.payload,
        regularPrice:
          action.payload?.regularPrice !== undefined
            ? action.payload.regularPrice
            : action.payload?.price,
      }

      newItem.price = toNumber(newItem.price)
      newItem.regularPrice = toNumber(newItem.regularPrice)
      newItem.premiumPrice = toNumber(newItem.premiumPrice)

      // Adjusted calculation for additional price from custom options
      const additionalPrice = calculateAdditionalPrice(newItem)

      const newTotalPrice = (newItem.price + additionalPrice) * newItem.quantity

      // Find the index of the product in the cart that has the same _id and size
      const index = state.products.findIndex(
        (item) => item._id === newItem._id && item.size === newItem.size
      )

      if (index !== -1) {
        // Update the quantity and totalPrice of the matched product
        state.products[index].quantity += newItem.quantity
        state.products[index].price = newItem.price
        state.products[index].regularPrice =
          newItem.regularPrice || state.products[index].regularPrice || state.products[index].price
        state.products[index].premiumPrice =
          newItem.premiumPrice || state.products[index].premiumPrice
        state.products[index].totalPrice =
          (state.products[index].price + calculateAdditionalPrice(state.products[index])) *
          state.products[index].quantity
      } else {
        // Add the new product to the cart, including additional price in totalPrice
        state.products.push({ ...newItem, totalPrice: newTotalPrice })
      }

      // Recalculate the cart's total, quantity, and totalTax
      recomputeCartTotals(state)

      toast('Product added to the cart!', {
        className: 'custom-toast-success',
        position: 'bottom-right',
        progressStyle: { background: 'yellow' },
      })
    },
    clearCart(state, action) {
      state.products = []
      state.quantity = 0
      state.total = 0
      state.totalTax = 0
      state.activeStep = 0
      state.billing = null
      state.email = ''
      state.customerMessage = ''
      toast('Varukorgen är tom!', {
        className: 'custom-toast-success',
        position: 'bottom-right',
        progressStyle: { background: 'yellow' },
      }) // Add a success toast message
    },
    deleteCart: (state, action) => {
      const updatedCart = state.products.filter(
        (item) => item._id !== action.payload
      )

      state.products = updatedCart

      // Recalculate the cart total and quantity after deleting a product
      recomputeCartTotals(state)

      toast('Produkt borttagen från varukorgen!', {
        className: 'custom-toast-success',
        position: 'bottom-right',
        progressStyle: { background: 'yellow' },
      })
    },
    onBackStep: (state) => {
      state.activeStep -= 1
    },

    onNextStep: (state) => {
      state.activeStep += 1
    },
    setFormData: (state, action) => {
      // Extract only serializable data from the file object, if it exists
      const serializableData = { ...action.payload }
      if (serializableData.img && serializableData.img instanceof File) {
        const { name, size, type } = serializableData.img
        // Store only the serializable parts of the file
        serializableData.img = { name, size, type }
        // Optionally, if you need to show a preview in the UI, store the URL as well
        serializableData.img.preview = URL.createObjectURL(serializableData.img)
      }

      state.formData = serializableData
    },
    resetFormData: (state) => {
      state.formData = null
    },
    getCartTotal(state) {
      recomputeCartTotals(state)
    },
    increaseQuantity: (state, action) => {
      const { productId, size } = action.payload
      const product = state.products.find(
        (item) => item._id === productId && item.size === size
      )

      if (product) {
        // Adjusted calculation for additional price from custom options
        const additionalPrice = calculateAdditionalPrice(product)

        // Update product quantity
        product.quantity += 1
        // Recalculate product's total price including additional price for each custom option
        product.totalPrice =
          (product.price + additionalPrice) * product.quantity
      }

      // Recalculate the cart total, quantity, and totalTax
      recomputeCartTotals(state)
    },
    decreaseQuantity: (state, action) => {
      const { productId, size } = action.payload
      const product = state.products.find(
        (item) => item._id === productId && item.size === size
      )

      if (product && product.quantity > 1) {
        // Ensure product.quantity > 1 to avoid negative quantities
        // Adjusted calculation for additional price from custom options
        const additionalPrice = calculateAdditionalPrice(product)

        // Update product quantity
        product.quantity -= 1
        // Recalculate product's total price including additional price for each custom option
        product.totalPrice =
          (product.price + additionalPrice) * product.quantity
      }

      // Recalculate the cart total, quantity, and totalTax after decreasing the product quantity
      recomputeCartTotals(state)
    },

    createBilling(state, action) {
      state.billing = action.payload
    },
    updateCheckoutId(state, action) {
      state.checkoutId = action.payload
    },
    updateAddress(state, action) {
      state.address = action.payload
    },
    updateEmail: (state, action) => {
      // New action to update email
      state.email = action.payload
    },
    updateCustomerMessage: (state, action) => {
      state.customerMessage = action.payload
    },
    setPartnerData: (state, action) => {
      state.currentPartner = action.payload.partnerData
    },
    clearPartnerData: (state) => {
      state.currentPartner = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCredentials, (state, action) => {
        if (action.payload?.is_paid === undefined || action.payload?.is_paid === null) return;
        repriceCartForMembership(state, Boolean(action.payload.is_paid));
      })
      .addCase(logOut, (state) => {
        repriceCartForMembership(state, false);
      });
  },
})
export const selectTotalTax = (state) => {
  return state.cart.products.reduce((totalTax, product) => {
    const productTax = (product.totalPrice * product.taxPercentage) / 100;
    return totalTax + productTax;
  }, 0);
};
export const { addProduct, clearCart, getCartTotal, updateCheckoutId, updateAddress, deleteCart, onBackStep, onNextStep, increaseQuantity, decreaseQuantity, createBilling, updateEmail, updateCustomerMessage, setFormData, resetFormData,  } = cartSlice.actions;
export default cartSlice.reducer;